/**
 * Payment state + inline action buttons. Mark Paid / Defer / Not Required open
 * dialogs orchestrated by the parent page; this section only renders the
 * state summary and trigger buttons. On-system styling (sm4rt-web-ui skill).
 */

import React from 'react';
import { IconExternalLink } from '@tabler/icons-react';
import type { CheckInSession } from '@techgitter/contracts';

import { Chip, toneFromMuiColor } from '../../chip';
import { deriveActionFlags, paymentChipColor, paymentLabel } from './sessionDetailHelpers';

export interface CheckInPaymentSectionProps {
  session: CheckInSession;
  onMarkPaid: () => void;
  onDeferPayment: () => void;
}

const fmt = (iso: string): string => {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-AU', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
};

/** The verb for the "by X on Y" line — the same two columns record who
 *  settled payment whichever way they settled it. */
function settledByVerb(state: string | null | undefined): string {
  if (state === 'deferred') return 'Deferred';
  if (state === 'deferred_awaiting_payment') return 'Invoice sent';
  if (state === 'not_required') return 'Marked not required';
  return 'Override';
}

export const CheckInPaymentSection: React.FC<CheckInPaymentSectionProps> = ({
  session,
  onMarkPaid,
  onDeferPayment,
}) => {
  const flags = deriveActionFlags(session);
  return (
    <div className="ci-section ci-section--tight">
      <div className="ci-chips">
        <Chip tone={toneFromMuiColor(paymentChipColor(session.paymentState))}>
          {paymentLabel(session.paymentState)}
        </Chip>
        {session.paymentReference && (
          <span className="ci-caption ci-mono">Ref: {session.paymentReference}</span>
        )}
      </div>

      {session.paymentOverriddenBy && session.paymentOverriddenAt && (
        <div className="ci-caption">
          {settledByVerb(session.paymentState)} by {session.paymentOverriddenBy} on{' '}
          {fmt(session.paymentOverriddenAt)}
        </div>
      )}

      {session.xeroOnlineInvoiceUrl && (
        <a
          className="ci-btn ci-btn--ghost ci-btn--sm"
          href={session.xeroOnlineInvoiceUrl}
          target="_blank"
          rel="noopener noreferrer"
          style={{ alignSelf: 'flex-start' }}
        >
          <IconExternalLink size={15} stroke={1.5} />
          Open Xero hosted payment
        </a>
      )}

      {(flags.canMarkPaid || flags.canDeferPayment) && (
        <div style={{ display: 'flex', gap: 8 }}>
          {flags.canMarkPaid && (
            <button type="button" className="ci-btn ci-btn--primary ci-btn--block" onClick={onMarkPaid}>
              Mark Paid
            </button>
          )}
          {flags.canDeferPayment && (
            <button type="button" className="ci-btn ci-btn--ghost ci-btn--block" onClick={onDeferPayment}>
              Defer / Not Required
            </button>
          )}
        </div>
      )}
    </div>
  );
};
